import React, { useState, useMemo, useEffect } from "react";
import "./style.css";
import useFetchReports from "../../hooks/useFetchReports";
import useFetchTestReports from "../../hooks/useFetchTestReports";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Chart from "react-apexcharts";
import { ReportLoader } from "../../components/loader";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import replayIcon from "../../assets/images/ReplayIcon.png";

// Progress Bar Component
const ProgressBar = ({ percentage }) => {
  const getColor = (percentage) => {
    if (percentage >= 80) return "#458C41";
    if (percentage >= 60) return "#DB9646";
    if (percentage >= 40) return "#C8C11B";
    return "#EE3131";
  };

  const backgroundColor = (() => {
    if (percentage >= 80) return "#B5CE97";
    if (percentage >= 60) return "#CFB392";
    if (percentage >= 40) return "#DEDB9C";
    return "#DF8D8D";
  })();

  return (
    <div
      className="progress-bar"
      style={{
        backgroundColor,
      }}
    >
      <div
        className="progress-fill"
        style={{
          width: `${percentage}%`,
          backgroundColor: getColor(percentage),
        }}
      />
    </div>
  );
};

// Custom Bar Component
const CustomBar = (props) => {
  const { fill, x, y, width, height, payload } = props;
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={payload.color}
      rx="8"
      ry="8"
    />
  );
};

// Report Card Component
const ReportCard = ({
  report,
  onClick,
  onViewMarks,
  isFirstLocked,
  firstLockedReport,
}) => {
  const { auth, setCommonWarning, breadcrumbs, setBreadcrumbs } = useAuth();
  const navigate = useNavigate();
  const percentage = report.mark ?? 0;
  // console.log("REPORT CARD:", report);
  // console.log("firstLockedData:", firstLockedReport);

  const isLocked = report.mark === 0;

  let bg;
  if (percentage >= 80) bg = "#DDE5B5";
  else if (percentage >= 60) bg = "#FFDAB1";
  else if (percentage >= 40) bg = "#FDFAB4";
  else bg = "#FFB7B7";

  return (
    <div
      className={`report-card ${isLocked ? "locked" : ""}`}
      style={{
        backgroundColor: isLocked ? "#e0e0e0" : bg,
        cursor: isLocked ? "not-allowed" : "pointer",
      }}
      onClick={isLocked ? null : onClick}
    >
      <h3 className="report-title">{report.name || "Untitled"}</h3>

      <ProgressBar percentage={percentage} />

      <p className="report-mark">{percentage}/100</p>

      {/* buttons only if not locked */}
      {!isLocked && (
        <>
          <div className="button-container">
            <button className="report-button">View Skills</button>
            <button
              className="report-button"
              onClick={(e) => {
                e.stopPropagation();
                onViewMarks(report);
              }}
            >
              View Marks
            </button>
          </div>

          <button
            className="replayIcon-holder"
            onClick={(e) => {
              e.stopPropagation();
              localStorage.setItem("sub_complete_modal_show", "false");

              const updatedBreadcrumbs = [...breadcrumbs];
              const catIndex = updatedBreadcrumbs.findIndex(
                (b) => b.path === "/categories"
              );
              const reportsIndex = updatedBreadcrumbs.findIndex(
                (b) => b.path === "/reports"
              );
              if (reportsIndex !== -1) {
                updatedBreadcrumbs[reportsIndex] = {
                  id: report?.id,
                  title: report?.name,
                  path: "/categories",
                };
              }
              if (catIndex !== -1) {
                updatedBreadcrumbs[catIndex] = {
                  id: report?.id,
                  title: report?.name,
                  path: `/categories`, // updated path
                };
              }
              const uniqueBreadcrumbs = updatedBreadcrumbs.filter(
                (item, index, self) =>
                  index === self.findIndex((b) => b.path === item.path)
              );
              setBreadcrumbs(uniqueBreadcrumbs);
              // console.log("Updated Breadcrumbs:", uniqueBreadcrumbs);
              navigate("/categories");
            }}
          >
            <img className="replayIcon" src={replayIcon} alt="report icon" />
          </button>
        </>
      )}

      {isLocked && (
        <div className="locked-div">
          <div className="locked-text">Not yet started</div>
          <div className="lock-icon">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#444"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
        </div>
      )}

      {isLocked && isFirstLocked && (
        <button
          className="start-button"
          onClick={(e) => {
            e.stopPropagation();
            // console.log("breadcrumos:", breadcrumbs);
            // console.log("firstLockedReport id", firstLockedReport?.id);
            // console.log("firstLockedReport title", firstLockedReport?.name);

            const updatedBreadcrumbs = [...breadcrumbs];
            const catIndex = updatedBreadcrumbs.findIndex(
              (b) => b.path === "/categories"
            );
            const reportsIndex = updatedBreadcrumbs.findIndex(
              (b) => b.path === "/reports"
            );
            if (reportsIndex) {
              // console.log("reportsIndex", reportsIndex)
              updatedBreadcrumbs[reportsIndex] = {
                id: firstLockedReport?.id,
                title: firstLockedReport?.name,
                path: `/categories`, // updated path
              };
            }
            if (catIndex !== -1) {
              updatedBreadcrumbs[catIndex] = {
                id: firstLockedReport?.id,
                title: firstLockedReport?.name,
                path: `/categories`, // updated path
              };
            }
            setBreadcrumbs(updatedBreadcrumbs);
            // console.log("Updated Breadcrumbs:", updatedBreadcrumbs);
            navigate("/categories");
          }}
        >
          Start
        </button>
      )}
    </div>
  );
};

// Gauge Chart Options
const gaugeOptions = {
  chart: {
    type: "radialBar",
    sparkline: { enabled: true },
    width: 1000,
  },
  plotOptions: {
    radialBar: {
      startAngle: -90,
      endAngle: 90,
      track: {
        background: "#e7e7e7",
        strokeWidth: "85%",
        margin: 5,
      },
      hollow: {
        margin: 3,
        size: "60%",
      },
      dataLabels: {
        name: { show: false },
        value: {
          offsetY: 0,
          fontSize: "42px",
          fontWeight: "800",
          fontFamily: "Alata, sans-serif",
          color: "#000000ff",
        },
      },
    },
  },
  colors: ["#4AD542"],
  stroke: {
    lineCap: "round",
  },
};

// color palette
const colorPalette = [
  "#a855f7",
  "#3b82f6",
  "#06b6d4",
  "#f97316",
  "#84cc16",
  "#eab308",
  "#c084fc",
  "#60a5fa",
  "#6b21a8",
  "#ec4899",
  "#10b981",
  "#78716c",
  "#fda4af",
  "#f87171",
  "#7ee8c4",
  "#facc15",
];

function convertSkillObjectToArray(skillObj = {}) {
  return Object.entries(skillObj).map(([key, value]) => ({
    skill: key,
    value: Math.round(Number(value) || 0),
  }));
}

// Main Reports Page Component
function ReportsPage() {
  const { reports, loading, error } = useFetchReports();
  const [activeTab, setActiveTab] = useState("chapterwise");
  const [currentPage, setCurrentPage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalReport, setModalReport] = useState(null);
  const [modalIndex, setModalIndex] = useState(null);
  const [showLoader, setShowLoader] = useState(true);
  const [marksModalOpen, setMarksModalOpen] = useState(false);
  const [marksModalIndex, setMarksModalIndex] = useState(null);

  const [isLoadingSkills, setIsLoadingSkills] = useState(false);
  const [isLoadingMarks, setIsLoadingMarks] = useState(false);

  const [windowWidth, setWindowWidth] = React.useState(
    typeof window !== "undefined" ? window.innerWidth : 1920
  );

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { itemsPerPage, needsPagination, totalPages } = useMemo(() => {
    let itemsPerRow = 5;

    if (windowWidth < 768) {
      itemsPerRow = 1;
    } else if (windowWidth < 900) {
      itemsPerRow = 2;
    } else if (windowWidth < 1024) {
      itemsPerRow = 3;
    } else if (windowWidth < 1200) {
      itemsPerRow = 3;
    } else if (windowWidth < 1440) {
      itemsPerRow = 4;
    }

    const itemsPerPage_final = itemsPerRow * 2;

    const needsPagination = reports.length > itemsPerPage_final;
    const totalPages = Math.ceil(reports.length / itemsPerPage_final);

    return { itemsPerPage: itemsPerPage_final, needsPagination, totalPages };
  }, [reports.length, windowWidth]);

  const paginatedReports = useMemo(() => {
    const start = currentPage * itemsPerPage;
    const end = start + itemsPerPage;
    return reports.slice(start, end);
  }, [reports, currentPage, itemsPerPage]);

  React.useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(0);
    }
  }, [totalPages, currentPage]);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const skillColorMapRef = React.useRef({});
  const getUniqueSkillColor = (skill) => {
    const map = skillColorMapRef.current;
    if (!map[skill]) {
      const index = Object.keys(map).length;
      map[skill] = colorPalette[index % colorPalette.length];
      // console.log("assign:", skill, map[skill]);
    }
    // console.log("use:", skill, map[skill]);
    return map[skill];
  };

  const openModal = (report) => {
    if (!report) return;
    const idx = reports.findIndex((r) => r.id === report.id);
    setIsLoadingSkills(true);

    const loadSkills = async () => {
      try {
        const res = await axios.post(
          `https://skillcalci.fefdybraingym.com/cal-by-game/predict`,
          report.questions_report,
          { headers: { "Content-Type": "application/json" } }
        );

        if (res.status === 200) {
          const skillImprovements =
            res.data?.[0]?.overall_skill_improvements ?? {};

          const skillArray = convertSkillObjectToArray(skillImprovements);
          const skillData = (skillArray || []).map((item) => ({
            skill: item.skill,
            value: Number(item.value),
            color: getUniqueSkillColor(item.skill),
          }));
          setModalReport({ report, data: skillData });
          setModalOpen(true);
        } else {
          setCommonWarning("Failed to load chart");
        }
      } catch (error) {
        console.error("Reports API ERROR", error);
        setCommonWarning("Failed to load reports");
      } finally {
        setIsLoadingSkills(false);
      }
    };

    loadSkills();
    setModalIndex(idx >= 0 ? idx : null);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalReport(null);
  };

  const openMarksModal = (report) => {
    if (!report) return;
    const idx = reports.findIndex((r) => r.id === report.id);
    setMarksModalIndex(idx >= 0 ? idx : null);
    setMarksModalOpen(true);
    setIsLoadingMarks(false);
  };

  const closeMarksModal = () => {
    setMarksModalOpen(false);
    setMarksModalIndex(null);
    setIsLoadingMarks(false);
  };

  const goToModalIndex = (index) => {
    if (index == null || index < 0 || index >= reports.length) return;
    const report = reports[index];
    if (!report) return;
    setIsLoadingSkills(true);
    let skillData = [];
    const loadSkills = async () => {
      try {
        const res = await axios.post(
          `https://skillcalci.fefdybraingym.com/cal-by-game/predict`,
          report.questions_report,
          { headers: { "Content-Type": "application/json" } }
        );

        // console.log("SKILL API RESPONSE:", res);
        // console.log("SKILL API RESPONSE DATA:", res.data);
        // console.log("SKILL API RESPONSE DATA [0] overall_skill_improvements:", [res.data[0].overall_skill_improvements]);

        if (res.status === 200) {
          // Local skill data from report.questions_report
          const skillImprovements =
            res.data?.[0]?.overall_skill_improvements ?? {};

          const skillArray = convertSkillObjectToArray(skillImprovements);
          skillData = (skillArray || []).map((item) => ({
            skill: item.skill,
            value: Number(item.value),
            color: getUniqueSkillColor(item.skill),
          }));
          setModalReport({ report, data: skillData });
          setModalOpen(true);
        } else {
          setCommonWarning("Failed to load chart");
        }
      } catch (error) {
        console.error("Reports API ERROR", error);
        setCommonWarning("Failed to load reports");
      } finally {
        setIsLoadingSkills(false);
      }
    };

    // call the async loader (you forgot to call it)
    loadSkills();

    setModalIndex(index);
  };

  const prevModal = () => {
    if (modalIndex == null) return;
    let newIndex = modalIndex - 1;

    while (newIndex >= 0 && reports[newIndex].mark === 0) {
      newIndex--;
    }

    if (newIndex >= 0) {
      goToModalIndex(newIndex);
    }
  };

  const nextModal = () => {
    if (modalIndex == null) return;
    let newIndex = modalIndex + 1;

    while (newIndex < reports.length && reports[newIndex].mark === 0) {
      newIndex++;
    }

    if (newIndex < reports.length) {
      goToModalIndex(newIndex);
    }
  };

  const goToMarksModalIndex = (index) => {
    if (index == null || index < 0 || index >= reports.length) return;
    setMarksModalIndex(index);
  };

  const prevMarksModal = () => {
    if (marksModalIndex == null) return;
    let newIndex = marksModalIndex - 1;

    while (newIndex >= 0 && reports[newIndex].mark === 0) {
      newIndex--;
    }

    if (newIndex >= 0) {
      goToMarksModalIndex(newIndex);
    }
  };

  const nextMarksModal = () => {
    if (marksModalIndex == null) return;

    let newIndex = marksModalIndex + 1;

    while (
      newIndex < testReports.length &&
      !testReports[newIndex]?.reports?.some((r) => r.got_mark > 0)
    ) {
      newIndex++;
    }

    if (newIndex < testReports.length) {
      setMarksModalIndex(newIndex);
    }
  };

  const hasNextUnlockedMarks = (currentIndex) => {
    for (let i = currentIndex + 1; i < testReports.length; i++) {
      const item = testReports[i];
      if (item?.reports?.some((r) => r.got_mark > 0)) {
        return true;
      }
    }
    return false;
  };

  const [chartData, setChartData] = useState([]);
  const [isReportHit, setIsReportHit] = useState(false);
  const { auth, setCommonWarning, breadcrumbs } = useAuth();
  const navigate = useNavigate();

  const [testReports, loading1] = useFetchTestReports();
  // console.log("TEST REPORTS:", testReports);

  useEffect(() => {
    const loadReportsByLevels = async () => {
      try {
        const level =
          breadcrumbs.find((item) => item.path === "/topics")?.id || null;

        if (!level) {
          setCommonWarning("Level not found");
          return;
        }

        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}reportsbylevel`,
          {
            token: auth.token,
            level: level,
          }
        );
        // console.log(res);
        if (res.status === 200) {
          const finalData = res.data.skills_report.map((item, index) => ({
            skill: item.skill,
            value: item?.value > 100 ? 100 : Number(item?.value || 0),
            color: getUniqueSkillColor(item.skill),
          }));
          setChartData(finalData);
        } else if (res.status === 400) {
          setCommonWarning(res.message);
        } else if (res.status === 401) {
          navigate("/login");
          return [];
        }
      } catch (error) {
        console.error("Reports API ERROR", error);
        setCommonWarning("Failed to load reports");
      }
    };

    loadReportsByLevels();
  }, [auth.token, breadcrumbs]);

  useEffect(() => {
    const timer = setTimeout(() => setShowLoader(false), 0);
    return () => clearTimeout(timer);
  }, []);

  const maxValue = Math.max(...chartData.map((s) => s.value), 0);
  const maxSkills = chartData.filter((skill) => skill.value === maxValue);
  const maxSkill = maxSkills[0] || { skill: "N/A", value: 0, color: "#000000" };

  return (
    <div className="reports-page-container">
      {loading || showLoader ? (
        <ReportLoader />
      ) : (
        <>
          <div className="tabs-container">
            <button
              className={`tab ${activeTab === "chapterwise" ? "active" : ""}`}
              onClick={() => setActiveTab("chapterwise")}
            >
              Chapterwise Reports
            </button>
            <button
              className={`tab ${activeTab === "fullskill" ? "active" : ""}`}
              onClick={() => setActiveTab("fullskill")}
            >
              View Full Skill Report
            </button>
          </div>

          {activeTab === "chapterwise" ? (
            <div className="reports-wrapper">
              <div className="reports-bg">
                {paginatedReports.map((report, index) => {
                  const isLocked = report.mark === 0;

                  const firstLockedIndex = paginatedReports.findIndex(
                    (r) => r.mark === 0
                  );

                  const firstLockedReport = paginatedReports.find(
                    (r) => r.mark === 0
                  );

                  return (
                    <ReportCard
                      key={report.id}
                      report={report}
                      isFirstLocked={isLocked && index === firstLockedIndex}
                      firstLockedReport={firstLockedReport}
                      onClick={() => openModal(report)}
                      onViewMarks={openMarksModal}
                    />
                  );
                })}
              </div>

              {needsPagination && (
                <div className="pagination-controls">
                  <button
                    className="pagination-btn prev-btn"
                    onClick={handlePrevPage}
                    disabled={currentPage === 0}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <span className="pagination-info">
                    {currentPage + 1} / {totalPages}
                  </span>
                  <button
                    className="pagination-btn next-btn"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages - 1}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="full-skill-report">
              <div className="skill-content">
                <div className="skill-left">
                  <div className="skill-message">
                    {maxSkills.length === 1 ? (
                      <>
                        <p>You're showing excellent improvement in</p>
                        <p
                          className="highlight"
                          style={{ color: maxSkill.color }}
                        >
                          {maxSkill.skill} Skills.
                        </p>

                        <div
                          style={{
                            width: "100%",
                            maxWidth: "800px",
                            margin: "0 auto",
                          }}
                        >
                          <Chart
                            options={{
                              ...gaugeOptions,
                              colors: [maxSkill.color],
                            }}
                            series={[maxSkill.value]}
                            type="radialBar"
                            height="400"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <p>You have highest skills in</p>
                        <div className="highlight">
                          {maxSkills.map((skill, idx) => (
                            <p key={idx} style={{ color: skill.color }}>
                              • {skill.skill}
                            </p>
                          ))}
                        </div>

                        <div
                          style={{
                            width: "100%",
                            maxWidth: "800px",
                            margin: "0 auto",
                          }}
                        >
                          <Chart
                            options={{
                              ...gaugeOptions,
                              colors: [maxSkill.color],
                            }}
                            series={[maxSkill.value]}
                            type="radialBar"
                            height="400"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="skill-right">
                  <div className="chart-header">
                    <p className="chart-subtitle">Statistics</p>
                    <h3 className="chart-title">
                      Your Full Skill in{" "}
                      <span className="level-badge">Explorer Level</span>
                    </h3>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={chartData.map((item) => ({
                        ...item,
                        color:
                          item.skill === maxSkill.skill
                            ? maxSkill.color
                            : item.color,
                      }))}
                      margin={{ top: 20, right: 30, left: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e0e0e0"
                        vertical={false}
                      />
                      <XAxis dataKey="skill" tick={false} axisLine={false} />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fontSize: 12 }}
                        ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                      />
                      <Tooltip
                        wrapperStyle={{ zIndex: 1000 }}
                        contentStyle={{
                          backgroundColor: "#fafafaff",
                          border: "1px solid #fdfdfdff",
                          borderRadius: "4px",
                        }}
                      />
                      <Bar
                        dataKey="value"
                        shape={<CustomBar />}
                        radius={[12, 12, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="chart-legend">
                    {chartData.map((item, idx) => (
                      <span
                        key={idx}
                        className="legend-item"
                        style={{
                          color:
                            item.skill === maxSkill.skill
                              ? maxSkill.color
                              : item.color,
                        }}
                      >
                        {item.skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modal for report chart */}
          {(modalOpen || isLoadingSkills) && (
            <div className="modal-overlay" onClick={closeModal}>
              {isLoadingSkills ? (
                <ReportLoader />
              ) : (
                <>
                  <button
                    className="modal-nav-btn modal-prev"
                    onClick={(e) => {
                      e.stopPropagation();
                      prevModal();
                    }}
                    disabled={
                      modalIndex == null ||
                      modalIndex <= 0 ||
                      reports.slice(0, modalIndex).every((r) => r.mark === 0)
                    }
                  >
                    &#10094;
                  </button>

                  <div
                    className="modal-content"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="modal-header">
                      <div className="modal-title">
                        {modalReport.report?.name || "Report Chart"}
                      </div>
                    </div>

                    <div className="modal-graph">
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={modalReport.data}
                          margin={{ top: 20, right: 30, left: 0 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e0e0e0"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="skill"
                            tick={false}
                            axisLine={false}
                          />
                          <YAxis
                            domain={[0, 100]}
                            tick={{ fontSize: 12 }}
                            ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                          />
                          <Tooltip
                            wrapperStyle={{ zIndex: 1000 }}
                            contentStyle={{
                              backgroundColor: "#fafafaff",
                              border: "1px solid #fdfdfdff",
                              borderRadius: "4px",
                            }}
                          />
                          <Bar
                            dataKey="value"
                            shape={<CustomBar />}
                            radius={[12, 12, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="chart-legend">
                      {modalReport.data.map((item, idx) => (
                        <span
                          key={idx}
                          className="legend-item"
                          style={{ color: item.color }}
                        >
                          {item.skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    className="modal-nav-btn modal-next"
                    onClick={(e) => {
                      e.stopPropagation();
                      nextModal();
                    }}
                    disabled={
                      modalIndex == null ||
                      modalIndex >= reports.length - 1 ||
                      reports.slice(modalIndex + 1).every((r) => r.mark === 0)
                    }
                  >
                    &#10095;
                  </button>
                </>
              )}
            </div>
          )}

          {/* Marks Modal */}
          {(marksModalOpen || isLoadingMarks) &&
            (() => {
              const item = testReports[marksModalIndex];
              const { title, outcomes, reports } = item || {};

              return (
                <div className="modal-overlay" onClick={closeMarksModal}>
                  {isLoadingMarks ? (
                    <ReportLoader />
                  ) : (
                    <>
                      <button
                        className="modal-nav-btn modal-prev"
                        onClick={(e) => {
                          e.stopPropagation();
                          prevMarksModal();
                        }}
                        disabled={
                          marksModalIndex === 0 ||
                          reports
                            ?.slice(0, marksModalIndex)
                            .every((r) => r.mark === 0)
                        }
                      >
                        &#10094;
                      </button>

                      <div
                        className="learn-mark"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h2 className="title">{title || "Report"}</h2>

                        <div className="mark-learn">
                          <div className="card">
                            <h1>Learning Outcomes</h1>
                            <p className="subtitle">
                              Children will now be able to:
                            </p>

                            <div className="outcomes-list">
                              {outcomes &&
                                outcomes.map((outcome, idx) => (
                                  <div key={idx} className="outcome-item">
                                    <div className="circle-container">
                                      <svg viewBox="0 0 70 70">
                                        <circle
                                          className="circle-bg"
                                          cx="35"
                                          cy="35"
                                          r="30"
                                        ></circle>
                                        <circle
                                          className="circle-progress"
                                          cx="35"
                                          cy="35"
                                          r="30"
                                        ></circle>
                                      </svg>
                                      <span className="checkmark">✓</span>
                                    </div>
                                    <div className="outcome-text">
                                      <p>{outcome}</p>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>

                          <div
                            className="result-card"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="circle c1"></div>
                            <div className="circle c2"></div>
                            <div className="circle c3"></div>

                            <h2 className="mark-title">Marks Summary</h2>

                            <div className="scores">
                              {reports &&
                                reports.map((r, i) => {
                                  return (
                                    <div key={i} className="score-row">
                                      <span>{r.name} :</span>
                                      <span>
                                        {r.got_mark}/{r.ttl_mark}
                                      </span>
                                    </div>
                                  );
                                })}

                              <div className="divider"></div>

                              <div className="score-row total">
                                <span>Total :</span>
                                <span>
                                  {(() => {
                                    const got = reports?.reduce(
                                      (a, b) => a + (b.got_mark || 0),
                                      0
                                    );
                                    const total = reports?.reduce(
                                      (a, b) => a + (b.ttl_mark || 0),
                                      0
                                    );
                                    return `${got} / ${total}`;
                                  })()}
                                </span>
                              </div>

                              <div className="percent-box">
                                <span>Percentage :</span>
                                <span>
                                  {(() => {
                                    const got = reports?.reduce(
                                      (a, b) => a + (b.got_mark || 0),
                                      0
                                    );
                                    const total = reports?.reduce(
                                      (a, b) => a + (b.ttl_mark || 0),
                                      0
                                    );
                                    if (total === 0) return "0%";
                                    return (
                                      Math.round((got / total) * 100) + "%"
                                    );
                                  })()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        className="modal-nav-btn modal-next"
                        onClick={(e) => {
                          e.stopPropagation();
                          nextMarksModal();
                        }}
                        disabled={
                          marksModalIndex >= testReports.length - 1 ||
                          !hasNextUnlockedMarks(marksModalIndex)
                        }
                      >
                        &#10095;
                      </button>
                    </>
                  )}
                </div>
              );
            })()}
        </>
      )}
    </div>
  );
}

export default ReportsPage;